import { ref } from 'vue'
import { agentApi } from '@/api/agent'

function revokeAgentBlobUrls(msgs) {
  for (const m of msgs || []) {
    const url = m?.mediaPreview?.url
    if (typeof url === 'string' && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url)
      } catch {
        /* ignore */
      }
    }
  }
}

const open = ref(false)
const messages = ref([])
const sessionId = ref(null)
const sending = ref(false)
const lastTools = ref([])
const activeController = ref(null)
const sessionHistory = ref([])
const historyLoading = ref(false)

function toggle() {
  open.value = !open.value
}

/**
 * 新建对话：仅清空面板；不删库，历史里仍可点开旧会话。
 */
function reset() {
  revokeAgentBlobUrls(messages.value)
  sessionId.value = null
  messages.value = []
  lastTools.value = []
}

async function fetchSessionHistory() {
  historyLoading.value = true
  try {
    const data = await agentApi.listSessions()
    sessionHistory.value = Array.isArray(data) ? data : []
  } finally {
    historyLoading.value = false
  }
}

async function restoreSession(targetSessionId) {
  if (!targetSessionId || sending.value) return
  const data = await agentApi.getSessionMessages(targetSessionId)
  sessionId.value = data?.session_id || targetSessionId
  revokeAgentBlobUrls(messages.value)
  messages.value = (data?.messages || []).map((m) => ({
    role: m.role,
    content: m.content,
    name: m.name,
    ts: m.ts,
    attachments: Array.isArray(m.attachments) ? m.attachments : [],
    completed: true,
  }))
  lastTools.value = []
}

async function renameHistorySession(targetSessionId, title) {
  if (!targetSessionId) return
  await agentApi.renameSession(targetSessionId, title)
  await fetchSessionHistory()
}

async function deleteHistorySession(targetSessionId) {
  if (!targetSessionId) return
  await agentApi.resetSession(targetSessionId)
  if (sessionId.value === targetSessionId) {
    sessionId.value = null
    revokeAgentBlobUrls(messages.value)
    messages.value = []
    lastTools.value = []
  }
  await fetchSessionHistory()
}

function clearForAuth() {
  sessionId.value = null
  revokeAgentBlobUrls(messages.value)
  messages.value = []
  lastTools.value = []
  sessionHistory.value = []
  sending.value = false
  if (activeController.value) {
    try {
      activeController.value.abort()
    } catch {
      /* ignore */
    }
    activeController.value = null
  }
}

async function send(message) {
  if (!message || sending.value) return

  const isTextMessage = typeof message === 'string'
  const text = isTextMessage ? message : message.text || ''
  const file = isTextMessage ? null : message.file || null
  const userDisplayText = text || (file ? `[已上传文件] ${file.name}` : '')
  if (!userDisplayText) return

  if (file) {
    const isVideo = file.type.startsWith('video/')
    const kind = isVideo ? 'video' : 'image'
    const previewUrl = URL.createObjectURL(file)
    const userLine = text.trim() || (isVideo ? `已上传视频 · ${file.name}` : `已上传图片 · ${file.name}`)
    messages.value.push({
      role: 'user',
      content: userLine,
      mediaPreview: { kind, url: previewUrl },
    })
  } else {
    messages.value.push({ role: 'user', content: userDisplayText })
  }

  if (file) {
    sending.value = true
    const controller = new AbortController()
    activeController.value = controller
    try {
      let history = []
      history = messages.value
        .slice(0, -1)
        .map((item) => ({ role: item.role, content: item.content }))

      const data = await agentApi.chat({
        message: text,
        sessionId: sessionId.value,
        history,
        file,
        signal: controller.signal,
      })

      sessionId.value = data.session_id
      lastTools.value = data.tool_calls || []
      const lastUser = messages.value[messages.value.length - 1]
      if (lastUser?.role === 'user' && Array.isArray(data.attachments) && data.attachments.length) {
        // 保留本地预览，避免立即切换到未签名 URL 导致破图/视频失效。
        // 历史会话恢复时，后端会返回可读签名地址。
        lastUser.attachments = data.attachments
      }
      messages.value.push({
        role: 'assistant',
        content: data.reply,
        tools: data.tool_calls,
        completed: true,
      })
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
        return
      }
      messages.value.push({
        role: 'assistant',
        content: `出错了：${e.message}`,
        completed: true,
      })
    } finally {
      if (activeController.value === controller) {
        activeController.value = null
      }
      sending.value = false
    }
    return
  }

  sending.value = true
  const controller = new AbortController()
  activeController.value = controller

  try {
    const assistantMsg = {
      role: 'assistant',
      content: '',
      tools: [],
      recommendations: [],
      completed: false,
    }
    messages.value.push(assistantMsg)
    const assistantIdx = messages.value.length - 1
    const getAssistant = () => messages.value[assistantIdx]

    let streamError = null
    await agentApi.chatStream({
      message: text,
      sessionId: sessionId.value,
      signal: controller.signal,
      onEvent: (event) => {
        if (!event || typeof event !== 'object') return
        if (event.type === 'meta') {
          sessionId.value = event.session_id || sessionId.value
          lastTools.value = event.tool_calls || []
          const current = getAssistant()
          if (current) {
            current.tools = event.tool_calls || []
          }
          return
        }
        if (event.type === 'delta') {
          const current = getAssistant()
          if (current) {
            current.content += event.content || ''
          }
          return
        }
        if (event.type === 'done') {
          const current = getAssistant()
          if (current) {
            current.completed = true
            if (Array.isArray(event.recommendations)) {
              current.recommendations = event.recommendations
            }
          }
          return
        }
        if (event.type === 'error') {
          streamError = new Error(event.message || 'stream error')
        }
      },
    })

    if (streamError) {
      throw streamError
    }
    const current = getAssistant()
    if (current && !current.content) {
      current.content = '模型未返回内容。'
      current.completed = true
    }
  } catch (e) {
    if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') {
      const lastIdx = messages.value.length - 1
      if (lastIdx >= 0 && messages.value[lastIdx]?.role === 'assistant') {
        messages.value[lastIdx].completed = true
        messages.value[lastIdx].recommendations = []
      }
      return
    }
    const lastIdx = messages.value.length - 1
    const last = lastIdx >= 0 ? messages.value[lastIdx] : null
    if (last?.role === 'assistant') {
      last.completed = true
      if (!String(last.content || '').trim()) {
        last.content = `出错了：${e.message}`
      } else {
        last.content = `${last.content}\n\n出错了：${e.message}`
      }
    } else {
      messages.value.push({
        role: 'assistant',
        content: `出错了：${e.message}`,
        completed: true,
      })
    }
  } finally {
    if (activeController.value === controller) {
      activeController.value = null
    }
    sending.value = false
  }
}

function stop() {
  const lastIdx = messages.value.length - 1
  if (lastIdx >= 0 && messages.value[lastIdx]?.role === 'assistant') {
    messages.value[lastIdx].completed = true
    messages.value[lastIdx].recommendations = []
  }
  if (!activeController.value) return
  activeController.value.abort()
  activeController.value = null
  sending.value = false
}

export function useAgentStore() {
  return {
    open,
    messages,
    sessionId,
    sending,
    lastTools,
    sessionHistory,
    historyLoading,
    toggle,
    send,
    stop,
    reset,
    fetchSessionHistory,
    restoreSession,
    renameHistorySession,
    deleteHistorySession,
    clearForAuth,
  }
}
